import { Injectable, inject } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable, catchError, map } from 'rxjs'

import { environment } from '../../environments/environment'
import { ErrorService } from './error.service'
import { BulkProductShareCsvTemplate } from '../types/productSharingCsvTemplates'

@Injectable({
  providedIn: 'root',
})
export class ProductSharingCsvTemplateService {
  private http = inject(HttpClient)
  private errorService = inject(ErrorService)

  private URL = environment.baseUrl + 'product-sharing-templates'

  /**
   * Creates a bulk product share CSV template.
   * @param {BulkProductShareCsvTemplate} template The template data
   * @returns {Observable<void>}
   */
  create(template: BulkProductShareCsvTemplate): Observable<void> {
    return this.http.post<void>(this.URL, template).pipe(
      map((res: void) => res),
      catchError(this.errorService.handleError),
    )
  }

  /**
   * Gets all bulk product share CSV templates for the authenticated user's company.
   * @returns {Observable<Array<BulkProductShareCsvTemplate>>} The templates found
   */
  getAll(): Observable<Array<BulkProductShareCsvTemplate>> {
    return this.http
      .get<Array<BulkProductShareCsvTemplate>>(this.URL)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Gets a bulk product share CSV template by id.
   * @param {string} id The template id
   * @returns {Observable<BulkProductShareCsvTemplate>} Result
   */
  getById(id: string): Observable<BulkProductShareCsvTemplate> {
    return this.http
      .get<BulkProductShareCsvTemplate>(this.URL + `/${id}`)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Updates a bulk product share CSV template.
   * @param {string} id The template id
   * @param {BulkProductShareCsvTemplate} updates The template updates
   * @returns {Observable<void>}
   */
  edit(id: string, updates: BulkProductShareCsvTemplate): Observable<void> {
    return this.http
      .put<void>(this.URL + `/${id}`, updates)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Deletes a bulk product share CSV template.
   * @param {string} id The template id
   * @returns {Observable<void>}
   */
  delete(id: string): Observable<void> {
    return this.http
      .delete<void>(this.URL + `/${id}`)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Downloads a bulk product share CSV template by id.
   * @param {string} id The template id
   * @returns {Observable<string>} Base64 result
   */
  download(id: string): Observable<string> {
    return this.http
      .get<{
        bulkProductShareCsvTemplate: string
      }>(this.URL + `/${id}/download`)
      .pipe(
        map((res) => res.bulkProductShareCsvTemplate),
        catchError(this.errorService.handleError),
      )
  }
}
